import { useNavigate } from "react-router-dom";
import { type SxProps, type Theme } from "@mui/material/styles";

import { AlertMessage } from "components/partials/flash/flash";
import RouterLink from "components/partials/router-link/router-link";

import { Alert } from "ui/alert";
import { Box } from "ui/box";
import { Button } from "ui/button";

import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

type AlertBoxProps = {
  alert: AlertMessage;
  handleClose?: () => void;
  showCloseButton?: boolean;
  sx?: SxProps<Theme>;
};

const LINK_ROW_SX: SxProps<Theme> = { mt: 1 };

const mappedLegacyVariants = (legacyVariant?: string) => {
  switch (legacyVariant) {
    case "danger":
      return { color: "error" as const };
    case "secondary":
      return { variant: "outlined" as const };
    case "tertiary":
      return { variant: "contained" as const };
    case "navy":
      return { color: "secondary" as const };
    case "primary":
    default:
      return {};
  }
};

// Similar to Flash component but doesn't use location state
const AlertBox = ({
  alert,
  handleClose = () => {},
  showCloseButton = true,
  sx,
}: AlertBoxProps) => {
  const navigate = useNavigate();

  const currentUser = useCurrentUser();

  const isClientUser = hasPermission(currentUser, Permission.PERM_CLIENT_USER);
  const { button, link, message, title, status } = alert;

  const handleButtonClick = () => {
    if (button?.onClick) {
      button.onClick();
    }
    if (button?.path) {
      navigate(button.path);
    }
  };

  return (
    <Alert
      severity={status}
      data-testid="flash-key"
      title={title}
      onClose={showCloseButton ? handleClose : undefined}
      actions={
        button &&
        !isClientUser && (
          <Button {...mappedLegacyVariants(button.variant)} onClick={handleButtonClick}>
            {button.label}
          </Button>
        )
      }
      sx={sx}>
      {message}
      {link && (
        <Box sx={LINK_ROW_SX}>
          <RouterLink to={link.route} sx={{ fontWeight: 700, textDecoration: "none" }}>
            {link.label}
          </RouterLink>
        </Box>
      )}
    </Alert>
  );
};

export default AlertBox;
